/**
 * Shared admin shell + routing helpers.
 *
 * - AdminProtected: gates an admin sub-route. Allowed admin_roles are
 *   ['super_admin', 'admin', 'support']. Non-admins are redirected to their
 *   normal dashboard (no 'Access denied' page so we don't tip them off).
 * - AdminShell: premium command-center layout with sidebar nav (desktop) and
 *   compact drawer (mobile). Branded with deep green + gold.
 */
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
    LayoutDashboard, Users, Heart, Bell,
    Activity, LogOut, ChevronLeft, Menu, ShieldCheck, Receipt,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { NiaMark } from '@/components/NiaLogo';

const ADMIN_ROLES = new Set(['super_admin', 'admin', 'support']);

export function isAdminUser(user) {
    if (!user) return false;
    const r = (user.admin_role || '').toLowerCase();
    return ADMIN_ROLES.has(r);
}

export function AdminProtected({ children, requireRole }) {
    const { user, loading, pinGate } = useAuth();
    const loc = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/" replace />;
    // Phase 17 — admins go through the PIN gate just like everyone else.
    if (pinGate === 'unlock' && loc.pathname !== '/pin/unlock') {
        return <Navigate to="/pin/unlock" replace state={{ from: loc.pathname }} />;
    }
    if (pinGate === 'setup' && loc.pathname !== '/pin/setup') {
        return <Navigate to="/pin/setup" replace state={{ from: loc.pathname }} />;
    }
    if (!isAdminUser(user)) {
        // V3.11 — non-admins are silently redirected to their home screen so we
        // never tip them off that an admin console exists. Recipients are now
        // a WhatsApp-first persona and have no in-app home, so we drop them at
        // the marketing root.
        const target = user.role === 'sender' ? '/sender/home' : '/';
        return <Navigate to={target} replace />;
    }
    if (requireRole && (user.admin_role || '').toLowerCase() !== requireRole) {
        return <Navigate to="/admin/overview" replace />;
    }
    return children;
}

// V1 admin sidebar.
//
// App Store positioning: the admin console must look like a family-support
// coordination tool, NOT a financial-services backoffice. Six items in the
// visible nav — Support Requests was removed alongside the rest of the v2
// WhatsApp/buffer pipeline.
//
// Super-admin tooling (Board KPIs / Audit Logs / Settings) remains
// reachable by URL for power users but is intentionally NOT exposed.
const NAV = [
    { to: '/admin/overview', label: 'Overview', icon: LayoutDashboard },
    { to: '/admin/analytics', label: 'Analytics', icon: Activity },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/family', label: 'Family', icon: Heart },
    { to: '/admin/receipts', label: 'Receipts', icon: Receipt },
    { to: '/admin/notifications', label: 'Notifications', icon: Bell },
];

export function AdminShell({ children, adminEmail }) {
    const loc = useLocation();
    const nav = useNavigate();
    const { logout, user } = useAuth();
    const [open, setOpen] = useState(false);

    // Close mobile drawer on route change
    useEffect(() => { setOpen(false); }, [loc.pathname]);

    const goAppView = () => {
        if (!user) return;
        if (user.role === 'sender') return nav('/sender/home');
        // Phase V3.9 — Recipient + Agent app surfaces retired; both
        // personas operate over WhatsApp now. Admins drop back to the
        // marketing root when no in-app destination applies.
        nav('/');
    };

    return (
        <div className="admin-shell">
            {/* Sidebar (desktop) + drawer (mobile) */}
            <aside className={`admin-sidebar ${open ? 'open' : ''}`} aria-label="Admin navigation">
                <div className="admin-brand">
                    <NiaMark size={28} variant="light" />
                    <div>
                        <div className="admin-brand-name">Nia Admin</div>
                        <div className="admin-brand-sub">Family Support Console</div>
                    </div>
                </div>

                <nav className="admin-nav" role="navigation">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
                            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\W+/g, '-')}`}
                        >
                            <n.icon size={18} />
                            <span>{n.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="admin-user-card">
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>Signed in</div>
                    <div style={{ fontWeight: 800, color: 'white', fontSize: 13 }}>{adminEmail || user?.email || '—'}</div>
                    <div className="admin-role-pill"><ShieldCheck size={11} /> {(user?.admin_role || 'admin').replace('_', ' ')}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                        <button onClick={goAppView} className="admin-secondary-btn" data-testid="admin-app-view">
                            <ChevronLeft size={12} /> App view
                        </button>
                        <button onClick={() => { logout(); nav('/', { replace: true }); }} className="admin-secondary-btn" data-testid="admin-logout">
                            <LogOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile header */}
            <header className="admin-mobile-header">
                <button onClick={() => setOpen(true)} aria-label="Open menu" className="admin-burger"><Menu size={20} /></button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NiaMark size={22} />
                    <div style={{ fontWeight: 900, color: 'var(--nia-text)' }}>Nia Admin</div>
                </div>
                <div style={{ width: 28 }} />
            </header>

            {/* Backdrop for mobile drawer */}
            {open && <button aria-label="Close menu" className="admin-backdrop" onClick={() => setOpen(false)} />}

            <main className="admin-main">{children}</main>
        </div>
    );
}

export function AdminSection({ title, subtitle, actions, children }) {
    return (
        <section className="admin-section">
            <div className="admin-section-header">
                <div>
                    <h1 className="admin-section-title">{title}</h1>
                    {subtitle && <p className="admin-section-sub">{subtitle}</p>}
                </div>
                {actions && <div className="admin-section-actions">{actions}</div>}
            </div>
            {children}
        </section>
    );
}

export { Link };
